/**
 * useStreamingChat
 *
 * Higher-level hook that wraps useStreamingStateMachine with SSE orchestration.
 * Handles chunk buffering, flushSync, state transitions, abort/error recovery —
 * consumers only provide callbacks for their context-specific logic.
 *
 */

import { useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useStreamingStateMachine, STREAMING_STATES } from './useStreamingStateMachine';
import { readSSEStream } from '../utils/readSSEStream';

// Shared localStorage key for streaming preference (same across all chat contexts)
const STREAMING_ENABLED_KEY = 'reedle-ai-streaming-enabled';

const getStoredStreamingEnabled = () => {
  try {
    const stored = localStorage.getItem(STREAMING_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

// Minimum characters to buffer before first display (avoids flicker, gives animation runway)
const BUFFER_CHAR_THRESHOLD = 50;

export { STREAMING_STATES };

export function useStreamingChat() {
  const {
    streamingState,
    streamingStateRef,
    streamingMessageIndexRef,
    abortControllerRef,
    forceCompleteStreaming,
    setForceCompleteStreaming,
    transitionStreamingState,
    handleAnimationComplete,
    stopStreaming,
    isStreamingActive,
  } = useStreamingStateMachine();

  // Shared mutable buffer — SSE chunks append here, animation RAF reads from here.
  // Decouples network from React: chunk arrivals never trigger re-renders.
  const streamBufferRef = useRef('');

  // Tracks how far the animation has progressed (character index).
  // Written by useSmoothStream on each RAF frame, read by abort handler to truncate buffer.
  const visibleLengthRef = useRef(0);

  // Streaming preference — user-facing toggle, persisted to localStorage.
  // Can be changed freely at any time, even mid-response.
  const [streamingEnabled, setStreamingEnabledState] = useState(getStoredStreamingEnabled);

  const setStreamingEnabled = useCallback((value) => {
    setStreamingEnabledState(value);
    try {
      localStorage.setItem(STREAMING_ENABLED_KEY, String(value));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Effective streaming mode — snapshotted from the preference at the start of
  // each response. Stays locked for the entire request lifecycle so that
  // mid-response toggling doesn't cause state machine / fetch mode mismatch.
  const activeStreamingModeRef = useRef(streamingEnabled);

  const captureStreamingMode = useCallback(() => {
    activeStreamingModeRef.current = streamingEnabled;
    return streamingEnabled;
  }, [streamingEnabled]);

  /**
   * Call before fetch to set up the streaming state machine.
   * @param {number} assistantMessageIndex - Index where the assistant message will appear
   */
  const startStreaming = useCallback((assistantMessageIndex) => {
    streamBufferRef.current = '';
    visibleLengthRef.current = 0;
    streamingMessageIndexRef.current = assistantMessageIndex;
    transitionStreamingState(STREAMING_STATES.NETWORK_STREAMING, 'sendMessage started');
  }, [transitionStreamingState]);

  /**
   * Call before fetch to set up the AbortController.
   * @returns {AbortController} controller to pass as fetch signal
   */
  const prepareFetch = useCallback(() => {
    setForceCompleteStreaming(false);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  }, [setForceCompleteStreaming]);

  /**
   * Process a streaming SSE response. Handles chunk buffering, state transitions,
   * abort/error recovery. Calls consumer callbacks for context-specific logic.
   *
   * @param {Response} response - fetch Response with SSE body
   * @param {object} callbacks
   * @param {(content: string) => *} callbacks.updateMessage - Update assistant message in React state. May return the message object.
   * @param {(sending: boolean) => void} callbacks.setSending - Controls the sending/typing indicator state
   * @param {(data: object, content: string) => void} [callbacks.onDone] - Called with the final SSE data payload and accumulated content
   * @param {(content: string, message: *) => void} [callbacks.onAbort] - Called when user stops streaming. Receives partial content and last message from updateMessage.
   * @param {(error: Error, content: string, message: *) => void} [callbacks.onError] - Called on stream error (not abort). Receives error, partial content, and last message.
   * @returns {Promise<{ content: string, message: * } | undefined>} Resolves with final content on success, undefined on abort
   */
  const processStreamingResponse = useCallback(async (response, {
    updateMessage,
    setSending,
    onDone,
    onAbort,
    onError,
  }) => {
    let shouldStartDisplaying = false;
    let lastMessage;

    try {
      await readSSEStream(response, {
        onChunk(delta) {
          // Append to shared buffer ref — no React state update, no re-render.
          // The animation RAF loop reads from streamBufferRef.current directly.
          streamBufferRef.current += delta;

          // First display: create the message in React state once we have enough chars
          if (!shouldStartDisplaying && streamBufferRef.current.length >= BUFFER_CHAR_THRESHOLD) {
            shouldStartDisplaying = true;
            flushSync(() => {
              lastMessage = updateMessage(streamBufferRef.current);
              setSending(false);
            });
          }
          // After threshold: no more updateMessage calls — animation reads from bufferRef
        },
        onDone(data) {
          if (!shouldStartDisplaying) {
            // Short response: never hit buffer threshold
            lastMessage = updateMessage(streamBufferRef.current);
            setSending(false);
            onDone?.(data, streamBufferRef.current);
            transitionStreamingState(STREAMING_STATES.FULLY_COMPLETE, 'short response');
          } else {
            // Sync final content to React state before transitioning
            lastMessage = updateMessage(streamBufferRef.current);
            onDone?.(data, streamBufferRef.current);
            transitionStreamingState(STREAMING_STATES.NETWORK_COMPLETE, 'SSE complete');
          }
        },
      });
    } catch (streamError) {
      if (streamError.name === 'AbortError') {
        // Truncate buffer to what was actually visible when user stopped.
        // visibleLengthRef is updated by useSmoothStream on each animation frame.
        if (streamBufferRef.current && visibleLengthRef.current > 0) {
          streamBufferRef.current = streamBufferRef.current.slice(0, visibleLengthRef.current);
        }
        if (streamBufferRef.current) {
          lastMessage = updateMessage(streamBufferRef.current);
        }
        onAbort?.(streamBufferRef.current, lastMessage);
        return undefined;
      }

      transitionStreamingState(STREAMING_STATES.ERROR, streamError.message);
      if (streamBufferRef.current && visibleLengthRef.current > 0) {
        streamBufferRef.current = streamBufferRef.current.slice(0, visibleLengthRef.current);
      }
      if (streamBufferRef.current) {
        lastMessage = updateMessage(streamBufferRef.current);
      }
      onError?.(streamError, streamBufferRef.current, lastMessage);
      transitionStreamingState(STREAMING_STATES.FULLY_COMPLETE, 'error recovery');
      throw streamError;
    }

    return { content: streamBufferRef.current, message: lastMessage };
  }, [transitionStreamingState]);

  /**
   * Abort any in-progress streaming and reset the state machine.
   * Call from clearChat or any cleanup path.
   */
  const cleanupStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (streamingStateRef.current !== STREAMING_STATES.IDLE &&
        streamingStateRef.current !== STREAMING_STATES.FULLY_COMPLETE) {
      setForceCompleteStreaming(true);
      transitionStreamingState(STREAMING_STATES.IDLE, 'chat cleared');
      streamingMessageIndexRef.current = null;
    }
    setForceCompleteStreaming(false); // always reset for next stream
  }, [transitionStreamingState, setForceCompleteStreaming]);

  // Wrap stopStreaming to truncate buffer at visible position.
  // Handles both cases: network still streaming (abort handler also truncates)
  // and network done but animation still playing (abort handler doesn't run).
  const stopStreamingWithTruncation = useCallback(() => {
    if (streamBufferRef.current && visibleLengthRef.current > 0 &&
        visibleLengthRef.current < streamBufferRef.current.length) {
      streamBufferRef.current = streamBufferRef.current.slice(0, visibleLengthRef.current);
    }
    stopStreaming();
  }, [stopStreaming]);

  // eslint-disable-next-line react-hooks/refs -- streamingMessageIndex is a latest-value ref synced by the state machine's callbacks; this app builds without React Compiler
  return {
    // State (from useStreamingStateMachine)
    streamingState,
    // eslint-disable-next-line react-hooks/refs -- latest-value ref synced by the state machine's callbacks; exposed here so consumers can render the active index without subscribing
    streamingMessageIndex: streamingMessageIndexRef.current,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming: stopStreamingWithTruncation,
    // Preference (user-facing toggle, freely changeable)
    streamingEnabled,
    setStreamingEnabled,
    // Effective mode (locked per response, call at start of sendMessage)
    captureStreamingMode,
    // Shared buffer (SSE writes, animation reads — no React in between)
    streamBufferRef,
    // Animation position (useSmoothStream writes, abort handler reads for truncation)
    visibleLengthRef,
    // Actions for sendMessage
    startStreaming,
    prepareFetch,
    processStreamingResponse,
    // Cleanup
    cleanupStreaming,
  };
}
